/**
 * Stream B — B23: Email Campaign A/B Testing UI
 *
 * Full A/B testing interface: create variants, set split %,
 * winning criteria, test duration, auto-send winner.
 * Tables: campaign_variants, campaign_ab_config (real schema).
 * Real API, no mocks.
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { FlaskConical, Plus, Trash2, Trophy, Clock, Send } from "lucide-react";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

const TEST_VARIABLES = ["subject", "content", "send_time", "from_name"] as const;
type TestVariable = (typeof TEST_VARIABLES)[number];

const WINNER_METRICS = ["open_rate", "click_rate", "conversion_rate"] as const;
type WinnerMetric = (typeof WINNER_METRICS)[number];

interface Variant {
  id: string;
  variantLabel: string;
  percentage: number;
  subjectOverride: string | null;
  fromNameOverride: string | null;
  sentCount: number;
  openCount: number;
  clickCount: number;
  conversionCount: number;
  isWinner: boolean;
}

interface AbConfig {
  testVariable: TestVariable;
  winnerMetric: WinnerMetric;
  winnerWaitHours: number;
  autoSendWinner: boolean;
  winnerDeterminedAt: string | null;
}

interface AbTestData {
  variants: Variant[];
  config: AbConfig | null;
}

const VARIABLE_LABELS: Record<TestVariable, string> = {
  subject: "Subject Line",
  content: "Email Content",
  send_time: "Send Time",
  from_name: "From Name",
};

const METRIC_LABELS: Record<WinnerMetric, string> = {
  open_rate: "Open Rate",
  click_rate: "Click Rate",
  conversion_rate: "Conversion Rate",
};

export default function AbTestingPage() {
  const { id: campaignId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AbTestData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editable config state
  const [testVariable, setTestVariable] = useState<TestVariable>("subject");
  const [winnerMetric, setWinnerMetric] = useState<WinnerMetric>("open_rate");
  const [winnerWaitHours, setWinnerWaitHours] = useState(4);
  const [autoSendWinner, setAutoSendWinner] = useState(true);
  const [variants, setVariants] = useState<Array<{ label: string; percentage: number; subject: string; fromName: string }>>([
    { label: "Variant A", percentage: 50, subject: "", fromName: "" },
    { label: "Variant B", percentage: 50, subject: "", fromName: "" },
  ]);

  const fetchData = useCallback(async () => {
    if (!campaignId) return;
    try {
      const res = await apiFetch<{ data: AbTestData }>(`/email-marketing/campaigns/${campaignId}/ab-test`);
      setData(res.data);
      if (res.data.config) {
        setTestVariable(res.data.config.testVariable);
        setWinnerMetric(res.data.config.winnerMetric);
        setWinnerWaitHours(res.data.config.winnerWaitHours);
        setAutoSendWinner(res.data.config.autoSendWinner);
      }
      if (res.data.variants.length > 0) {
        setVariants(res.data.variants.map((v) => ({
          label: v.variantLabel,
          percentage: v.percentage,
          subject: v.subjectOverride ?? "",
          fromName: v.fromNameOverride ?? "",
        })));
      }
    } catch {
      setData({ variants: [], config: null });
    } finally {
      setIsLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPercentage = variants.reduce((sum, v) => sum + v.percentage, 0);

  const updateVariant = (idx: number, patch: Partial<{ label: string; percentage: number; subject: string; fromName: string }>) => {
    setVariants((prev) => prev.map((v, i) => (i === idx ? { ...v, ...patch } : v)));
  };

  const handleAddVariant = () => {
    const nextLetter = String.fromCharCode(65 + variants.length);
    const evenSplit = Math.floor(100 / (variants.length + 1));
    setVariants((prev) => [
      ...prev.map((v) => ({ ...v, percentage: evenSplit })),
      { label: `Variant ${nextLetter}`, percentage: 100 - evenSplit * variants.length, subject: "", fromName: "" },
    ]);
  };

  const handleRemoveVariant = (idx: number) => {
    if (variants.length <= 2) return;
    const updated = variants.filter((_, i) => i !== idx);
    const evenSplit = Math.floor(100 / updated.length);
    setVariants(updated.map((v, i) => ({
      ...v,
      percentage: i === updated.length - 1 ? 100 - evenSplit * (updated.length - 1) : evenSplit,
    })));
  };

  const handleSave = async () => {
    if (!campaignId) return;
    if (totalPercentage !== 100) {
      setError("Split percentages must total 100%");
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await apiFetch(`/email-marketing/campaigns/${campaignId}/ab-test`, {
        method: "PUT",
        body: JSON.stringify({
          config: { testVariable, winnerMetric, winnerWaitHours, autoSendWinner },
          variants: variants.map((v) => ({
            variantLabel: v.label,
            percentage: v.percentage,
            subjectOverride: v.subject || null,
            fromNameOverride: v.fromName || null,
          })),
        }),
      });
      await fetchData();
    } catch {
      setError("Failed to save A/B test configuration");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48 rounded" />
        {[1, 2, 3].map((i) => <SkeletonCard key={i} className="h-24 rounded-xl" />)}
      </div>
    );
  }

  const hasResults = data?.variants.some((v) => v.sentCount > 0) ?? false;
  const winner = data?.variants.find((v) => v.isWinner);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FlaskConical className="h-5 w-5 text-[var(--color-primary)]" />
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">A/B Test</h1>
            <p className="text-sm text-[var(--color-muted-foreground)]">Campaign {campaignId?.slice(0, 8)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/email/campaigns`)}
            className="rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-sm text-[var(--color-foreground)] hover:bg-[var(--color-surface)]"
          >
            Back
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || totalPercentage !== 100}
            className="rounded-lg bg-[var(--color-primary)] px-4 py-1.5 text-sm text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-[var(--color-error)]/20 bg-[var(--color-error)]/5 px-4 py-3 text-sm text-[var(--color-error)]">
          {error}
        </div>
      )}

      {/* Winner banner */}
      {winner && (
        <div className="flex items-center gap-3 rounded-xl border border-[var(--color-success)]/20 bg-[var(--color-success)]/5 p-4">
          <Trophy className="h-5 w-5 text-[var(--color-success)]" />
          <div>
            <p className="font-medium text-[var(--color-success)]">Winner: {winner.variantLabel}</p>
            <p className="text-sm text-[var(--color-muted-foreground)]">
              {data?.config?.winnerDeterminedAt
                ? `Determined ${new Date(data.config.winnerDeterminedAt).toLocaleString()}`
                : "Winner selected"}
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Left: Config */}
        <div className="space-y-4">
          {/* Test variable */}
          <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <h3 className="mb-3 font-medium text-[var(--color-foreground)]">Test Variable</h3>
            <select
              value={testVariable}
              onChange={(e) => setTestVariable(e.target.value as TestVariable)}
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
            >
              {TEST_VARIABLES.map((v) => (
                <option key={v} value={v}>{VARIABLE_LABELS[v]}</option>
              ))}
            </select>
          </div>

          {/* Winning criteria */}
          <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <h3 className="mb-3 font-medium text-[var(--color-foreground)]">Winning Criteria</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-[var(--color-muted-foreground)]">Metric</label>
                <select
                  value={winnerMetric}
                  onChange={(e) => setWinnerMetric(e.target.value as WinnerMetric)}
                  className="mt-1 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                >
                  {WINNER_METRICS.map((m) => (
                    <option key={m} value={m}>{METRIC_LABELS[m]}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-[var(--color-muted-foreground)]">Wait before declaring winner</label>
                <div className="mt-1 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-[var(--color-muted-foreground)]" />
                  <input
                    type="number"
                    min={1}
                    max={168}
                    value={winnerWaitHours}
                    onChange={(e) => {
                      const parsed = Number(e.target.value);
                      if (!Number.isNaN(parsed) && parsed >= 1) setWinnerWaitHours(parsed);
                    }}
                    className="w-20 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
                  />
                  <span className="text-sm text-[var(--color-muted-foreground)]">hours</span>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[var(--color-foreground)]">
                <input
                  type="checkbox"
                  checked={autoSendWinner}
                  onChange={(e) => setAutoSendWinner(e.target.checked)}
                  className="rounded"
                />
                <Send className="h-3.5 w-3.5" />
                Auto-send winner to remaining audience
              </label>
            </div>
          </div>

          {/* Variants */}
          <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-medium text-[var(--color-foreground)]">Variants</h3>
              {variants.length < 5 && (
                <button
                  onClick={handleAddVariant}
                  className="inline-flex items-center gap-1 text-sm text-[var(--color-primary)] hover:underline"
                >
                  <Plus className="h-3.5 w-3.5" /> Add variant
                </button>
              )}
            </div>

            {totalPercentage !== 100 && (
              <p className="mb-2 text-xs text-[var(--color-error)]">
                Split must total 100% (currently {totalPercentage}%)
              </p>
            )}

            <div className="space-y-3">
              {variants.map((v, idx) => (
                <div key={idx} className="stagger-item rounded-lg bg-[var(--color-surface)] p-3">
                  <div className="flex items-center justify-between mb-2">
                    <input
                      value={v.label}
                      onChange={(e) => updateVariant(idx, { label: e.target.value })}
                      className="font-medium text-sm bg-transparent text-[var(--color-foreground)] border-none outline-none"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={5}
                        max={95}
                        value={v.percentage}
                        onChange={(e) => {
                          const parsed = Number(e.target.value);
                          if (!Number.isNaN(parsed)) updateVariant(idx, { percentage: parsed });
                        }}
                        className="w-16 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1 text-sm text-center text-[var(--color-foreground)]"
                      />
                      <span className="text-xs text-[var(--color-muted-foreground)]">%</span>
                      {variants.length > 2 && (
                        <button onClick={() => handleRemoveVariant(idx)} className="text-[var(--color-error)] hover:opacity-70">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {(testVariable === "subject" || testVariable === "content") && (
                    <input
                      placeholder={testVariable === "subject" ? "Subject line override..." : "Content variant description..."}
                      value={v.subject}
                      onChange={(e) => updateVariant(idx, { subject: e.target.value })}
                      className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-sm text-[var(--color-foreground)]"
                    />
                  )}
                  {testVariable === "from_name" && (
                    <input
                      placeholder="From name override..."
                      value={v.fromName}
                      onChange={(e) => updateVariant(idx, { fromName: e.target.value })}
                      className="w-full rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-1.5 text-sm text-[var(--color-foreground)]"
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Split bar visualization */}
            <div className="mt-3 flex h-3 overflow-hidden rounded-full">
              {variants.map((v, idx) => {
                const colors = [
                  "bg-[var(--color-primary)]",
                  "bg-[var(--color-ai)]",
                  "bg-[var(--color-success)]",
                  "bg-[var(--color-warning)]",
                  "bg-[var(--color-error)]",
                ];
                return (
                  <div
                    key={idx}
                    className={`${colors[idx % colors.length]} transition-all`}
                    style={{ width: `${v.percentage}%` }}
                    title={`${v.label}: ${v.percentage}%`}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Right: Results */}
        <div className="space-y-4">
          <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <h3 className="mb-3 font-medium text-[var(--color-foreground)]">Results</h3>
            {!hasResults ? (
              <div className="rounded-lg bg-[var(--color-surface)] p-8 text-center">
                <FlaskConical className="mx-auto h-8 w-8 text-[var(--color-muted-foreground)] mb-2" />
                <p className="text-sm text-[var(--color-muted-foreground)]">Results will appear after the test is sent.</p>
                <p className="text-xs text-[var(--color-muted-foreground)] mt-1">Save configuration, then send the campaign to start the test.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(data?.variants ?? []).map((v) => {
                  const openRate = v.sentCount > 0 ? (v.openCount / v.sentCount) * 100 : 0;
                  const clickRate = v.openCount > 0 ? (v.clickCount / v.openCount) * 100 : 0;
                  const convRate = v.sentCount > 0 ? (v.conversionCount / v.sentCount) * 100 : 0;

                  return (
                    <div key={v.id} className={`stagger-item rounded-lg border p-3 ${v.isWinner ? "border-[var(--color-success)] bg-[var(--color-success)]/5" : "border-[var(--color-border)]"}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-sm text-[var(--color-foreground)]">
                          {v.variantLabel}
                          {v.isWinner && <Trophy className="inline ml-1 h-3.5 w-3.5 text-[var(--color-success)]" />}
                        </span>
                        <span className="text-xs text-[var(--color-muted-foreground)]">{v.sentCount} sent</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-xs text-[var(--color-muted-foreground)]">Opens</p>
                          <p className="text-sm font-bold text-[var(--color-foreground)]">{openRate.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-[var(--color-muted-foreground)]">Clicks</p>
                          <p className="text-sm font-bold text-[var(--color-foreground)]">{clickRate.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-xs text-[var(--color-muted-foreground)]">Conversions</p>
                          <p className="text-sm font-bold text-[var(--color-foreground)]">{convRate.toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Statistical significance placeholder */}
          {hasResults && (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
              <h3 className="mb-2 font-medium text-[var(--color-foreground)]">Statistical Significance</h3>
              <p className="text-sm text-[var(--color-muted-foreground)]">
                Significance is calculated after sufficient data is collected. The winning variant
                needs at least 95% confidence to be declared winner.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
